/**
 * Helpers for POST /api/whsales — bill-level metal wholesale (Ratecut-like).
 * Lines = metal OUT gm only. Settlement = paid_gm / baki_gm / rate at bill footer.
 */

import { parsePaymentModeFromApi } from "./paymentLinesErpAdapter";
import { readPaymentIdempotencyKeyFromApi } from "./paymentIdempotency";

export const ERP_WHSALES_LIST_INVALIDATE_EVENT = "erp:whsales-list-invalidate";

export const isWhsalesApiSuccess = (res) =>
  res &&
  (res.success === true ||
    res.status === true ||
    String(res.status || "").toLowerCase() === "success" ||
    res.message === "radha");

export const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

export function createWhsalesIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Line weight used for totals (prefer fine → nwt → gwt by item_rate_on). */
export function lineWeightGm(line = {}) {
  const on = String(line.item_rate_on ?? line.itemRateOn ?? "NWT")
    .trim()
    .toUpperCase();
  if (on === "GWT" || on === "GWT.") {
    return num(line.gwt ?? line.grWt ?? line.gross_wt, 0);
  }
  if (on === "FINE" || on === "FWT" || on === "FINE_WT") {
    return num(line.fine_wt ?? line.fineWt ?? line.nwt ?? line.netWt, 0);
  }
  return num(line.nwt ?? line.netWt ?? line.fine_wt ?? line.fineWt, 0);
}

export function sumOutGm(items = []) {
  if (!Array.isArray(items)) return 0;
  return items.reduce((s, it) => s + lineWeightGm(it), 0);
}

export function sumOldMetalInGm(oldMetal = [], billMetalType = "GOLD") {
  if (!Array.isArray(oldMetal)) return 0;
  const want = String(billMetalType || "GOLD").trim().toUpperCase();
  return oldMetal.reduce((s, row) => {
    const mt = String(row.metal_type ?? row.metalType ?? want)
      .trim()
      .toUpperCase();
    if (mt && mt !== want) return s;
    return (
      s +
      num(
        row.fine_wt ??
          row.fineWt ??
          row.nwt ??
          row.netWt ??
          row.gwt ??
          row.grWt,
        0,
      )
    );
  }, 0);
}

/** baki_gm = total_out − old_in(same metal) − paid_gm (≥ 0) */
export function calcBakiGm(totalOutGm, oldInGm, paidGm) {
  return Math.max(0, +(num(totalOutGm) - num(oldInGm) - num(paidGm)).toFixed(3));
}

export function calcGrandTotal(paidGm, ratePerGm) {
  return +(num(paidGm) * num(ratePerGm)).toFixed(2);
}

export function bakiStatusLabel(bakiGm, metalType = "GOLD") {
  const b = num(bakiGm);
  const metal =
    String(metalType || "GOLD").toUpperCase() === "SILVER" ? "Silver" : "Gold";
  if (b <= 0) return `0.000 Cr ${metal}`;
  return `${b.toFixed(3)} Dr ${metal}`;
}

export function pickWhsaleRowFromResponse(res) {
  if (!res?.data) return null;
  if (res.data.whsale_details) return res.data.whsale_details;
  if (res.data.whsale) return res.data.whsale;
  if (Array.isArray(res.data) && res.data.length) return res.data[0];
  if (
    typeof res.data === "object" &&
    !Array.isArray(res.data) &&
    (res.data.id != null ||
      Array.isArray(res.data.items) ||
      res.data.doc_number)
  ) {
    return res.data;
  }
  return null;
}

export function extractWhsalesListRowsFromResponseData(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.whsales)) return data.whsales;
  if (Array.isArray(data.whsale_details)) return data.whsale_details;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.records)) return data.records;
  return [];
}

export function mapWhsaleToListRow(row = {}) {
  if (row == null || row.id == null) return null;
  const items = Array.isArray(row.items) ? row.items : [];
  const oldMetal = Array.isArray(row.old_metal)
    ? row.old_metal
    : Array.isArray(row.oldMetal)
      ? row.oldMetal
      : [];
  const metalType = String(row.metal_type || row.metalType || "GOLD").toUpperCase();
  const totalOut =
    row.total_out_gm != null
      ? num(row.total_out_gm)
      : sumOutGm(items);
  const oldIn =
    row.old_metal_in_gm != null
      ? num(row.old_metal_in_gm)
      : sumOldMetalInGm(oldMetal, metalType);
  const paidGm = num(row.paid_gm ?? row.paidGm, 0);
  const bakiGm =
    row.baki_gm != null
      ? num(row.baki_gm)
      : calcBakiGm(totalOut, oldIn, paidGm);
  const rate = num(row.item_rate ?? row.itemRate ?? row.rate, 0);
  const grand = num(
    row.grand_total ?? row.grandTotal,
    calcGrandTotal(paidGm, rate),
  );

  return {
    id: row.id,
    doc_number: row.doc_number || row.docNumber || row.voucher_number || "",
    financial_year: row.financial_year || row.financialYear || null,
    date: row.doc_date || row.date || row.bill_date || "",
    customer_id: row.customer_id ?? row.customerId ?? null,
    customer_name: row.customer_name || row.customerName || row.name || "N/A",
    customer_mobile: row.customer_mobile || row.customerMobile || row.mobile || "",
    metal_type: metalType,
    total_out_gm: totalOut,
    old_metal_in_gm: oldIn,
    paid_gm: paidGm,
    baki_gm: bakiGm,
    item_rate: rate,
    grand_total: grand,
    remaining_amount: num(
      row.remaining_amount ?? row.money_left ?? row.moneyLeft,
      0,
    ),
    money_left: num(row.money_left ?? row.moneyLeft ?? row.remaining_amount, 0),
    notes: row.notes || row.note || "",
    status: row.status,
  };
}

export function normalizeListWhsalesRows(res) {
  if (!isWhsalesApiSuccess(res) || res.data == null) return [];
  return extractWhsalesListRowsFromResponseData(res.data)
    .map(mapWhsaleToListRow)
    .filter(Boolean);
}

export function getWhsaleDetailsFromShowResponse(res) {
  if (!isWhsalesApiSuccess(res)) return null;
  const row = pickWhsaleRowFromResponse(res);
  if (!row) return null;

  const items = Array.isArray(row.items) ? row.items : [];
  const oldMetal = Array.isArray(row.old_metal)
    ? row.old_metal
    : Array.isArray(row.oldMetal)
      ? row.oldMetal
      : [];
  const payments = Array.isArray(row.payments) ? row.payments : [];
  const metalType = String(row.metal_type || row.metalType || "GOLD").toUpperCase();
  const totalOut =
    row.total_out_gm != null ? num(row.total_out_gm) : sumOutGm(items);
  const oldIn =
    row.old_metal_in_gm != null
      ? num(row.old_metal_in_gm)
      : sumOldMetalInGm(oldMetal, metalType);
  const paidGm = num(row.paid_gm ?? row.paidGm, 0);
  const rate = num(
    row.item_rate ?? row.itemRate ?? row.rate_per_gm ?? row.ratePerGm,
    0,
  );
  const bakiGm =
    row.baki_gm != null
      ? num(row.baki_gm)
      : row.gm_left != null
        ? num(row.gm_left)
        : calcBakiGm(totalOut, oldIn, paidGm);

  return {
    id: row.id,
    doc_number: row.doc_number || row.docNumber || "",
    financial_year: row.financial_year || null,
    date: row.doc_date || row.date || "",
    customer_id: row.customer_id ?? row.customerId ?? null,
    customer_name: row.customer_name || row.name || "",
    customer_mobile: row.customer_mobile || row.mobile || "",
    customer_gold_balance_gm: num(
      row.customer_gold_balance_gm ?? row.customerGoldBalanceGm,
      0,
    ),
    customer_silver_balance_gm: num(
      row.customer_silver_balance_gm ?? row.customerSilverBalanceGm,
      0,
    ),
    metal_type: metalType,
    item_rate: rate,
    paid_gm: paidGm,
    baki_gm: bakiGm,
    remaining_amount: num(
      row.remaining_amount ?? row.money_left ?? row.moneyLeft,
      0,
    ),
    money_left: num(row.money_left ?? row.moneyLeft ?? row.remaining_amount, 0),
    conv_percent: num(row.conv_percent ?? row.convPercent, 0),
    grand_total: num(row.grand_total, calcGrandTotal(paidGm, rate)),
    notes: row.notes || row.note || "",
    total_out_gm: totalOut,
    old_metal_in_gm: oldIn,
    items,
    old_metal: oldMetal,
    payments: payments.map((p, i) => ({
      ...p,
      id: p.id ?? p.transaction_id ?? i + 1,
      amount: num(p.amount, 0),
      pay_mode: parsePaymentModeFromApi(p.payment_mode || p.mode),
      payment_mode: p.payment_mode || p.mode,
      bank_account_id: p.bank_account_id ?? null,
      idempotency_key: readPaymentIdempotencyKeyFromApi(p) || undefined,
    })),
  };
}

/** Map UI payment rows → API payments (mode + amount + bank_account_id). */
export function mapWhsalePaymentsForApi(payments = []) {
  if (!Array.isArray(payments)) return [];
  return payments
    .map((p) => {
      const amount = num(p.amount ?? p.amt, 0);
      if (amount <= 0) return null;
      const modeRaw = String(
        p.mode || p.payment_mode || p.pay_mode || "CASH",
      )
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "_");
      const mode =
        modeRaw === "BANK_TRANSFER"
          ? "BANK"
          : modeRaw === "BANK"
            ? "BANK"
            : modeRaw;
      const bankId = p.bank_account_id ?? p.bankAccountId ?? null;
      const numBank = Number(bankId);
      return {
        mode,
        payment_mode: mode,
        amount,
        bank_account_id:
          Number.isFinite(numBank) && numBank > 0 ? numBank : null,
        ...(p.idempotency_key
          ? { idempotency_key: p.idempotency_key }
          : {}),
        ...(p.remarks ? { remarks: p.remarks } : {}),
      };
    })
    .filter(Boolean);
}

export function defaultRateFromMasters(metalType, masterGoldRate, masterSilverRate) {
  const isSilver = String(metalType || "").toUpperCase() === "SILVER";
  const raw = isSilver ? masterSilverRate : masterGoldRate;
  if (Array.isArray(raw)) {
    const v = num(raw[0] ?? raw[3] ?? raw[2], 0);
    return v;
  }
  return num(raw, 0);
}

/**
 * Pick 1gm rate from daily-price gold array using metal-group carat.
 * Array order: [24, 22, 18, 18 rose, 9, 9 rose]
 */
export function resolveGoldRateIndexForCarat(carat, label = "") {
  const caratNum = Number(carat);
  const p = Number.isFinite(caratNum) ? Math.round(caratNum) : null;
  const labelL = String(label || "").toLowerCase();

  if (labelL.includes("rose")) {
    if (p === 9) return 5;
    if (p === 18) return 3;
    return 3;
  }
  if (p === 22) return 1;
  if (p === 18) return 2;
  if (p === 9) return 4;
  if (p === 24) return 0;
  return 0;
}

/** 1gm rate from metal-group carat + master rates (Sales-style). */
export function rateFromMetalGroupCarat({
  carat,
  metal = "Gold",
  label = "",
  masterGoldRate,
  masterSilverRate,
  showLiveRate = false,
} = {}) {
  const isSilver = String(metal || "").toLowerCase().includes("silver");
  if (isSilver) {
    const base = num(masterSilverRate, 0);
    return showLiveRate ? base / 1000 : base;
  }

  if (Array.isArray(masterGoldRate)) {
    const idx = resolveGoldRateIndexForCarat(carat, label);
    return num(masterGoldRate[idx], 0);
  }

  const base = num(masterGoldRate, 0);
  const perGram = showLiveRate ? base / 10 : base;
  const caratNum = Number(carat);
  const k =
    Number.isFinite(caratNum) && caratNum > 0 && caratNum <= 24
      ? caratNum
      : 24;
  return +(perGram * (k / 24)).toFixed(2);
}
